import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  UserCheck, 
  Truck, 
  Store, 
  Package, 
  ShoppingBag,
  ShoppingCart,
  Award
} from 'lucide-react';

interface DashboardStatsResponse {
  community: {
    total_users: number;
    clients: number;
    producers: number;
    transporters: number;
    recenseurs: number;
  };
  coverage: {
    regions: number;
    markets: number;
    markets_with_validated_data: number;
  };
  products: {
    reference_products: number;
    marketplace_products: number;
    categories: number;
  };
  market_intelligence: {
    validated_price_collections: number;
    products_with_price_data: number;
  };
  transport: {
    vehicles: number;
  };
  commerce: {
    orders: number;
    delivered_orders: number;
  };
}

interface StatItemProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  color?: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon, value, label, color = 'text-green-600' }) => {
  return (
    <div className="group relative flex min-h-[126px] flex-col justify-between overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_8px_24px_rgba(31,41,55,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/20 hover:shadow-[0_14px_30px_rgba(31,41,55,0.1)]">
      <div className="absolute inset-x-0 top-0 h-1 bg-primary/10 transition-colors duration-300 group-hover:bg-gradient-to-r from-primary via-third to-secondary" />
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 ${color} transition-transform duration-300 group-hover:scale-110`}>
          {icon}
        </div>
        <span className="text-[10px] font-bold tracking-[0.2em] text-gray-300">{String(value).padStart(2, '0')}</span>
      </div>
      <div className="mt-4">
        <p className="text-2xl font-black tracking-tight text-gray-900">{value.toLocaleString()}</p>
        <p className="mt-1 line-clamp-1 text-xs font-medium text-gray-500">{label}</p>
      </div>
    </div>
  );
};

interface StatsGridProps {
  data: DashboardStatsResponse;
}

const DashboardStats: React.FC<StatsGridProps> = ({ data }) => {
  const { t } = useTranslation();

  // Statistiques principales
  const stats = [
    {
      icon: <Users size={30} strokeWidth={2.2} />,
      value: data.community.total_users,
      label: t('membres_communaute') || 'Membres dans la communauté',
      color: 'text-primary'
    },
    {
      icon: <UserCheck size={30} strokeWidth={2.2} />,
      value: data.community.producers,
      label: t('producteurs') || 'Producteurs',
      color: 'text-primary'
    },
    {
      icon: <Store size={30} strokeWidth={2.2} />,
      value: data.coverage.markets,
      label: t('points_de_vente') || 'Points de vente',
      color: 'text-third'
    },
    {
      icon: <Package size={30} strokeWidth={2.2} />,
      value: data.products.reference_products,
      label: t('produits_reference') || 'Produits référencés',
      color: 'text-primary'
    },
    {
      icon: <ShoppingBag size={30} strokeWidth={2.2} />,
      value: data.products.marketplace_products,
      label: t('produits_marketplace') || 'Produits en marketplace',
      color: 'text-primary'
    },
    {
      icon: <Truck size={30} strokeWidth={2.2} />,
      value: data.transport.vehicles,
      label: t('vehicules') || 'Véhicules',
      color: 'text-third'
    },
    {
      icon: <ShoppingCart size={30} strokeWidth={2.2} />,
      value: data.commerce.orders,
      label: t('commandes') || 'Commandes',
      color: 'text-secondary'
    },
    {
      icon: <Award size={30} strokeWidth={2.2} />,
      value: data.community.transporters + data.community.recenseurs,
      label: t('ambassadeurs_actifs') || 'Ambassadeurs actifs',
      color: 'text-primary'
    }
  ];

  return (
    <div className="w-full">
      {/* En-tête */}
      <div className="mb-7 text-center flex flex-col items-center justify-center ">
        <h2 className="text-2xl font-black tracking-tight text-gray-900 md:text-3xl">
          {t('communaute_agribusiness') || 'Une communauté agribusiness en pleine croissance'}
        </h2>
        <div className="mt-3 flex items-center gap-2">
          <div className="h-1 w-3 rounded-full bg-secondary" />
          <div className="h-1 w-12 rounded-full bg-primary" />
          <div className="h-1 w-3 rounded-full bg-third" />
        </div>
      </div>

      {/* Grille des statistiques */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <StatItem key={index} {...stat} />
        ))}
      </div>

      
    </div>
  );
};

export default DashboardStats;